import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';

import { getJob, publishJob, updateJobDueDate, updateJobReference } from '@/api/jobs';
import { getReview } from '@/api/review';
import { AiLoading } from '@/components/ui/ai-loading/ai-loading';
import { Button } from '@/components/ui/button/button';
import { Card } from '@/components/ui/card/card';
import { StatusTag } from '@/components/ui/status-tag/status-tag';
import { useAuthStore } from '@/stores/authStore';
import type {
  Evidence,
  JobOut,
  MySubmissionOut,
  Question,
  SubmissionOut,
} from '@/types';
import { dueCountdown, formatDateTime } from '@/utils/due';

import styles from './job-detail.module.css';

const isObjective = (t?: string | null) => t === 'choice' || t === 'fill_blank';
const letterOf = (i: number) => String.fromCharCode(65 + i);

export function JobDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const queryClient = useQueryClient();
  const jobId = Number(id);
  const [publishing, setPublishing] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['job', jobId],
    queryFn: () => getJob(jobId),
    enabled: !!jobId,
  });

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      await publishJob(jobId);
      await queryClient.invalidateQueries({ queryKey: ['job', jobId] });
      await queryClient.invalidateQueries({ queryKey: ['jobs'] });
      showToast('发布成功，学生现在可以作答了');
    } catch {
      showToast('发布失败，请重试');
    } finally {
      setPublishing(false);
    }
  };

  if (isLoading) return <div className={styles.loading}>加载中...</div>;
  if (!data) return <div className={styles.loading}>作业不存在</div>;

  const { job, submissions, my_submission, submitted_count, class_member_count } = data;
  const isTeacher = user?.role === 'teacher';
  const cd = dueCountdown(job.due_date);
  const isOverdue = cd.level === 'overdue';

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>{job.title}</h1>
          <p className={styles.subtitle}>
            {job.subject} · 满分 {job.max_score} · 布置 {formatDateTime(job.created_at)}
            <span className={isOverdue ? styles.subtitleOverdue : undefined}>
              {job.due_date ? ` · 截止 ${formatDateTime(job.due_date)}` : ' · 截止未设置'}
            </span>
          </p>
        </div>
        <StatusTag status={job.status} />
      </header>

      {/* 教师端：截止时间管理（过期可延长，让未完成学生继续作答） */}
      {isTeacher && job.status === 'published' && (
        <DueDateCard
          job={job}
          onSaved={() => {
            queryClient.invalidateQueries({ queryKey: ['job', jobId] });
            queryClient.invalidateQueries({ queryKey: ['jobs'] });
            showToast('截止时间已更新');
          }}
        />
      )}

      <Card className={styles.infoCard}>
        <h2 className={styles.sectionTitle}>作业描述</h2>
        <p className={styles.desc}>{job.description || '暂无描述'}</p>
        {isObjective(job.type) ? (
          <QuestionsPreview job={job} showAnswer={isTeacher} />
        ) : (
          <>
            <h2 className={styles.sectionTitle}>评分维度</h2>
            <ul className={styles.dimensions}>
              {job.dimensions.map((dim) => (
                <li key={dim.name} className={styles.dimensionItem}>
                  <span>{dim.name}</span>
                  <span>
                    {dim.max_score} 分 / 权重 {dim.weight}
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
      </Card>

      {/* 参考答案 / 参考范文：教师可编辑并控制开放；学生仅在教师开放后可见 */}
      <ReferenceCard
        job={job}
        isTeacher={isTeacher}
        onSaved={() => {
          queryClient.invalidateQueries({ queryKey: ['job', jobId] });
          showToast('参考答案设置已保存');
        }}
      />

      {isTeacher && job.status === 'draft' && (
        <div className={styles.actions}>
          <Button loading={publishing} onClick={handlePublish}>
            发布作业
          </Button>
        </div>
      )}

      {/* 学习通式：学生"我的提交"状态卡 */}
      {!isTeacher && job.status === 'published' && (
        <Card className={styles.mySubCard}>
          <div className={styles.mySubHeader}>
            <h2 className={styles.sectionTitle}>我的提交</h2>
            {my_submission && <MySubmissionStatus sub={my_submission} />}
          </div>

          {isOverdue && (
            <div className={styles.overdueBox}>
              ⏰ 作业已过截止时间（{job.due_date ? new Date(job.due_date).toLocaleString() : ''}），无法提交或修改。如需继续完成，请联系老师延长截止时间。
            </div>
          )}

          {!my_submission && (
            <>
              <p className={styles.mySubHint}>你还没有提交这份作业</p>
              <div className={styles.actions}>
                <Button
                  onClick={() => navigate(`/jobs/${jobId}/submit`)}
                  disabled={isOverdue}
                >
                  提交作业
                </Button>
              </div>
            </>
          )}

          {my_submission && (
            <>
              <div className={styles.mySubMeta}>
                <span>提交时间:{' '}
                  {new Date(my_submission.submitted_at).toLocaleString()}
                </span>
                {my_submission.score !== null && my_submission.score !== undefined && (
                  <span>
                    得分：{my_submission.score} / {my_submission.max_score}
                  </span>
                )}
              </div>

              {my_submission.review_status === 'rejected' && (
                <div className={styles.rejectBox}>
                  <strong>老师已打回：</strong>
                  {my_submission.reject_reason || '请修改后重新提交'}
                </div>
              )}

              {my_submission.review_comment && (
                <p className={styles.commentBox}>
                  <strong>教师评语：</strong>
                  {my_submission.review_comment}
                </p>
              )}

              <div className={styles.actions}>
                {!isOverdue && (
                  <Button onClick={() => navigate(`/jobs/${jobId}/submit`)}>
                    {my_submission.review_status === 'rejected'
                      ? '重新提交'
                      : my_submission.status === 'reviewed'
                        ? '修改提交'
                        : '修改提交'}
                  </Button>
                )}
                {my_submission.status === 'reviewed' && (
                  <Button
                    variant="secondary"
                    onClick={() => navigate(`/review/${my_submission.id}`)}
                  >
                    查看批阅反馈
                  </Button>
                )}
              </div>
            </>
          )}
        </Card>
      )}

      {/* 学生：AI 批改完整报告（维度得分 / 扣分点 / 批注高亮原文 / AI 评语） */}
      {!isTeacher && my_submission && my_submission.status !== 'pending' && (
        <StudentAiReport submissionId={my_submission.id} />
      )}

      {isTeacher && (
        <>
          <div className={styles.submitStats}>
            <span className={styles.statItem}>
              已交 <b>{submitted_count ?? 0}</b>
            </span>
            {class_member_count ? (
              <span className={styles.statItem}>
                未交 <b>{Math.max(0, class_member_count - (submitted_count ?? 0))}</b>
              </span>
            ) : null}
            <span className={styles.statItem}>
              班级人数 <b>{class_member_count || '-'}</b>
            </span>
          </div>

          <h2 className={styles.sectionTitle}>提交记录</h2>
          <div className={styles.submissions}>
            {submissions.map((sub) => (
              <Card key={sub.id} className={styles.subCard}>
                <div className={styles.subHeader}>
                  <span className={styles.studentName}>{sub.student_name}</span>
                  <SubStatus sub={sub} />
                </div>
                <div className={styles.subMeta}>
                  <span>得分：{sub.score ?? '-'}</span>
                  <span>提交时间：{new Date(sub.submitted_at).toLocaleString()}</span>
                </div>
                <div className={styles.subActions}>
                  <Button
                    size="small"
                    onClick={() => navigate(`/review/${sub.id}`)}
                  >
                    批阅
                  </Button>
                </div>
              </Card>
            ))}
            {submissions.length === 0 && (
              <div className={styles.empty}>暂无提交记录</div>
            )}
          </div>
        </>
      )}

      {toast && (
        <div className={styles.toast} role="status">
          {toast}
        </div>
      )}
    </div>
  );
}

function QuestionsPreview({ job, showAnswer }: { job: JobOut; showAnswer: boolean }) {
  const questions: Question[] = job.questions ?? [];
  return (
    <>
      <h2 className={styles.sectionTitle}>
        题目（共 {questions.length} 题，满分 {job.max_score}）
        {showAnswer ? ' · 教师视图（含答案）' : ' · 提交后可查看逐题批改'}
      </h2>
      <div className={styles.qList}>
        {questions.map((q, idx) => (
          <div key={q.id} className={styles.qItem}>
            <div className={styles.qStem}>
              <span className={styles.qIndex}>{idx + 1}.</span>
              <span>{q.stem}</span>
              <span className={styles.qScoreTag}>{q.score}分</span>
            </div>
            {q.type === 'fill_blank' ? (
              showAnswer ? (
                <div className={styles.qAnswer}>
                  参考答案：
                  {(Array.isArray(q.answer) ? q.answer : []).map((a, i) => (
                    <span key={i} className={styles.qAnswerTag}>
                      第{i + 1}空 {String(a).replace(/\|/g, ' / ')}
                    </span>
                  ))}
                </div>
              ) : (
                <div className={styles.qAnswerHidden}>
                  共 {Array.isArray(q.answer) ? q.answer.length : 0} 个空，作答后可查看批改结果
                </div>
              )
            ) : (
              <ul className={styles.qOptions}>
                {(q.options ?? []).map((opt, oi) => {
                  const correct =
                    q.type === 'multiple_choice'
                      ? Array.isArray(q.answer) && (q.answer as string[]).includes(letterOf(oi))
                      : q.answer === letterOf(oi);
                  return (
                    <li key={oi} className={styles.qOption}>
                      <span className={styles.qOptionLetter}>{letterOf(oi)}</span>
                      <span>{opt}</span>
                      {showAnswer && correct && (
                        <span className={styles.qCorrect}>✓ 正确</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        ))}
      </div>
    </>
  );
}

function MySubmissionStatus({ sub }: { sub: MySubmissionOut }) {
  if (sub.review_status === 'rejected') return <StatusTag status="rejected" />;
  if (sub.status === 'reviewed') return <StatusTag status="reviewed" />;
  return <StatusTag status="pending" />;
}

function SubStatus({ sub }: { sub: SubmissionOut }) {
  if (sub.review_status === 'rejected') return <StatusTag status="rejected" />;
  if (sub.status === 'reviewed') return <StatusTag status="reviewed" />;
  return <StatusTag status="pending" />;
}

/* ---------------- 教师端：截止时间管理 ---------------- */

function DueDateCard({
  job,
  onSaved,
}: {
  job: JobOut;
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  const cd = dueCountdown(job.due_date);
  const isOverdue = cd.level === 'overdue';

  const toLocalInput = (iso?: string) => {
    if (!iso) return '';
    const d = new Date(iso);
    const tzOffset = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - tzOffset).toISOString().slice(0, 16);
  };

  const handleEdit = () => {
    setValue(toLocalInput(job.due_date) || toLocalInput(new Date(Date.now() + 86400000).toISOString()));
    setEditing(true);
  };

  const handleSave = async () => {
    if (!value) return;
    setSaving(true);
    try {
      const iso = new Date(value).toISOString();
      await updateJobDueDate(job.id, iso);
      setEditing(false);
      onSaved();
    } catch {
      /* 忽略 */
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className={styles.dueDateCard}>
      <div className={styles.dueDateHeader}>
        <h2 className={styles.sectionTitle}>截止时间</h2>
        {editing ? (
          <div className={styles.dueDateActions}>
            <input
              type="datetime-local"
              className={styles.dueDateInput}
              value={value}
              onChange={(e) => setValue(e.target.value)}
            />
            <Button size="small" loading={saving} onClick={handleSave}>
              保存
            </Button>
            <Button size="small" variant="secondary" onClick={() => setEditing(false)}>
              取消
            </Button>
          </div>
        ) : (
          <div className={styles.dueDateActions}>
            {job.due_date ? (
              <span className={`${styles.dueDateValue} ${isOverdue ? styles.dueOverdue : ''}`}>
                {new Date(job.due_date).toLocaleString()}{' '}
                {isOverdue ? '（已过期）' : `（${cd.text}）`}
              </span>
            ) : (
              <span className={styles.dueDateValue}>未设置截止时间</span>
            )}
            <Button size="small" variant="secondary" onClick={handleEdit}>
              {isOverdue ? '延长截止时间' : '修改截止时间'}
            </Button>
          </div>
        )}
      </div>
      {isOverdue && !editing && (
        <p className={styles.dueDateHint}>
          作业已过截止时间，未提交的学生无法继续作答。点击「延长截止时间」可重新设定，让学生继续完成。
        </p>
      )}
    </Card>
  );
}

/* ---------------- 参考答案 / 参考范文 ---------------- */

function ReferenceCard({
  job,
  isTeacher,
  onSaved,
}: {
  job: JobOut;
  isTeacher: boolean;
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [answer, setAnswer] = useState(job.reference_answer ?? '');
  const [show, setShow] = useState(!!job.show_reference);
  const [saving, setSaving] = useState(false);

  const hasReference = !!job.reference_answer?.trim();

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateJobReference(job.id, {
        reference_answer: answer,
        show_reference: show,
      });
      setEditing(false);
      onSaved();
    } catch {
      /* 忽略，保持编辑态 */
    } finally {
      setSaving(false);
    }
  };

  const handleToggleShow = async () => {
    const next = !show;
    setShow(next);
    try {
      await updateJobReference(job.id, { show_reference: next });
      onSaved();
    } catch {
      setShow(!next);
    }
  };

  // 学生视角：教师未开放时不显示
  if (!isTeacher && !job.show_reference) return null;
  if (!isTeacher && !hasReference) return null;

  return (
    <Card className={styles.refCard}>
      <div className={styles.refHeader}>
        <h2 className={styles.sectionTitle}>
          {isObjective(job.type) ? '参考答案' : '参考答案 / 参考范文'}
        </h2>
        {isTeacher ? (
          <div className={styles.refHeaderActions}>
            <label className={styles.refToggle}>
              <input
                type="checkbox"
                checked={show}
                onChange={handleToggleShow}
                disabled={saving}
              />
              向学生开放
            </label>
            {!editing ? (
              <Button size="small" variant="secondary" onClick={() => setEditing(true)}>
                编辑
              </Button>
            ) : (
              <Button size="small" loading={saving} onClick={handleSave}>
                保存
              </Button>
            )}
          </div>
        ) : (
          <span className={styles.refOpenTag}>老师已开放</span>
        )}
      </div>

      {!editing && (
        <p className={styles.refContent}>
          {job.reference_answer || '教师尚未填写参考答案。'}
        </p>
      )}

      {editing && (
        <div className={styles.refEditor}>
          <textarea
            className={styles.refTextarea}
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            rows={6}
            placeholder="填写参考答案或范文要点，学生端将在开放后可见"
          />
          <p className={styles.refHint}>
            提示：勾选「向学生开放」后，学生在作业详情页可查看参考答案。
          </p>
        </div>
      )}
    </Card>
  );
}

/* ---------------- 学生端：AI 批改完整报告 ---------------- */

function StudentAiReport({ submissionId }: { submissionId: number }) {
  const { data, isLoading } = useQuery({
    queryKey: ['review', submissionId],
    queryFn: () => getReview(submissionId),
  });

  if (isLoading)
    return (
      <AiLoading
        title="AI 批改报告加载中"
        hint={['正在调取批改结果…', '正在整理维度得分…', '正在标注扣分位置…']}
      />
    );
  if (!data) return null;

  const deductions = data.evidences.filter((e) => (e.deduction ?? 0) > 0);

  return (
    <Card className={styles.reportCard}>
      <div className={styles.reportHeader}>
        <h2 className={styles.sectionTitle}>AI 批改报告</h2>
        <span className={styles.reportScore}>
          {data.score} / {data.max_score} 分
        </span>
      </div>

      {/* 维度得分 */}
      {data.dimensions.length > 0 && (
        <div className={styles.dimScores}>
          {data.dimensions.map((d) => {
            const pct = d.max_score > 0 ? Math.round(((d.score ?? 0) / d.max_score) * 100) : 0;
            return (
              <div key={d.name} className={styles.dimRow}>
                <span className={styles.dimName}>{d.name}</span>
                <div className={styles.dimBar}>
                  <div className={styles.dimBarFill} style={{ width: `${pct}%` }} />
                </div>
                <span className={styles.dimValue}>
                  {d.score ?? 0}/{d.max_score}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* 客观题：逐题得分明细 */}
      {data.details && data.details.length > 0 && (
        <div className={styles.evidenceBlock}>
          <h3 className={styles.annotatedTitle}>逐题得分</h3>
          <ul className={styles.evidenceList}>
            {data.details.map((q, idx) => (
              <li
                key={q.id}
                className={`${styles.evidenceItem} ${
                  q.is_correct ? styles.evidenceOk : ''
                }`}
              >
                <div className={styles.evidenceHead}>
                  <span className={styles.evidenceDim}>
                    第 {idx + 1} 题 · {q.is_correct ? '✓ 正确' : '✗ 错误'}
                  </span>
                  <span className={styles.evidenceDeduction}>
                    {q.earned}/{q.max_score} 分
                  </span>
                </div>
                <p className={styles.evidenceDesc}>{q.stem}</p>
                {!q.is_correct && (
                  <p className={styles.evidenceLoc}>
                    你的答案：
                    {Array.isArray(q.student_answer)
                      ? q.student_answer.join('、')
                      : q.student_answer ?? '未作答'}
                    {'　'}正确答案：
                    {Array.isArray(q.correct_answer)
                      ? q.correct_answer.join('、')
                      : q.correct_answer}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* 原文 + 批注高亮 */}
      {data.content && !isObjective(data.job_type) && (
        <div className={styles.annotatedBlock}>
          <h3 className={styles.annotatedTitle}>我的作答（AI 已标记扣分位置）</h3>
          <AnnotatedContent content={data.content} evidences={deductions} />
        </div>
      )}

      {/* 扣分点明细 */}
      {deductions.length > 0 && (
        <div className={styles.evidenceBlock}>
          <h3 className={styles.annotatedTitle}>扣分点与错误原因</h3>
          <ul className={styles.evidenceList}>
            {deductions.map((ev) => (
              <li key={ev.id} className={styles.evidenceItem}>
                <div className={styles.evidenceHead}>
                  <span className={styles.evidenceDim}>{ev.dimension}</span>
                  <span className={styles.evidenceDeduction}>-{ev.deduction} 分</span>
                </div>
                <p className={styles.evidenceDesc}>{ev.description}</p>
                {ev.location && (
                  <p className={styles.evidenceLoc}>位置：{ev.location}</p>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* AI 评语 */}
      {data.comment && (
        <div className={styles.aiComment}>
          <strong>AI 评语：</strong>
          {data.comment}
        </div>
      )}

      {deductions.length === 0 && (
        <div className={styles.aiComment}>太棒了，本次作答没有被扣分点！</div>
      )}
    </Card>
  );
}

/** 在原文上高亮 AI 批注位置（evidence.location 命中原文片段时标记） */
function AnnotatedContent({ content, evidences }: { content: string; evidences: Evidence[] }) {
  const locations = evidences
    .map((e) => (e.location ?? '').trim())
    .filter((loc) => loc && loc !== '全文' && loc.length >= 2 && content.includes(loc));

  if (locations.length === 0) {
    return <pre className={styles.annotatedText}>{content}</pre>;
  }

  const escaped = locations
    .map((loc) => loc.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .sort((a, b) => b.length - a.length);
  const regex = new RegExp(`(${escaped.join('|')})`, 'g');
  const parts = content.split(regex);

  return (
    <pre className={styles.annotatedText}>
      {parts.map((part, i) =>
        locations.includes(part) ? (
          <mark key={i} className={styles.annotationMark} title="AI 标记的扣分位置">
            {part}
          </mark>
        ) : (
          part
        ),
      )}
    </pre>
  );
}
